import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
} from "react-router";
import * as Sentry from "@sentry/react-router";
import { useEffect } from "react";
import "@fontsource-variable/hanken-grotesk";
import "@fontsource-variable/bricolage-grotesque";
import "./tailwind.css";
import { NavigationProgress } from "./components/navigation-progress";
import { ThemeScript } from "~providers/theme-provider";
import { ErrorHero } from "@sunfish/ui";

export function headers() {
  return {
    "Cache-Control": "no-cache, no-store, must-revalidate",
  };
}

export function meta() {
  return [
    {
      name: "view-transition",
      content: "same-origin",
    },
  ];
}

export function links() {
  return [
    { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
    { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
    { rel: "icon", href: "/favicon-16x16.png", sizes: "16x16", type: "image/png" },
    { rel: "icon", href: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
    { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
  ];
}

export default function App() {
  // ThemeScript puts the resolved theme class on documentElement before
  // hydration so the page never flashes the wrong theme. That server/client
  // difference is the point, so React is told not to report it.
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <ThemeScript />
      </head>
      <body>
        <NavigationProgress />
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  useEffect(() => {
    if (!isRouteErrorResponse(error)) {
      Sentry.captureException(error);
    }
  }, [error]);

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ErrorHero
          fullPage
          code={is404 ? "404" : "500"}
          title={is404 ? "This page swam off." : "Choppy waters."}
          body={
            is404
              ? "It happens to the best of us. Let's get you back to open water."
              : "Something broke on our end. We're on it. Try again in a moment."
          }
          actionLabel="Back to shore"
          actionHref="/events"
        />
        <Scripts />
      </body>
    </html>
  );
}
